import _ from 'lodash'
import { Tsg, prn } from '@sparky/framework'
import { ARRAY, OBJECT } from '../../../../../common/utils/Utils'
import { IDENTITY_TYPES } from '../../../../constants/IAMConstants'
import type {
  IAM_APIAccessPolicy,
  IAM_APIServiceAccount,
  IAM_Role
} from '../../../../../common/reducers/types/IAMReducer.types'

export const isChildOf: (tenant: Tsg, selected: Tsg) => boolean = (tenant, selected) => {
  let found = false
  if (ARRAY.hasArray(tenant.children)) {
    const children = tenant.children || []
    for (const child of children) {
      if (child?.id === selected?.id) {
        return true
      }
      if (ARRAY.hasArray(child.children)) {
        found = isChildOf(child, selected)
        if (found) {
          return found
        }
      }
    }
  }
  return found
}

export const selectLowerTSG = (tsgA: Tsg, tsgB: Tsg) => {
  if (!tsgA) {
    return tsgB
  }
  if (!tsgB) {
    return tsgA
  }
  if (isChildOf(tsgA, tsgB)) {
    return tsgB
  }
  if (isChildOf(tsgB, tsgA)) {
    return tsgA
  }
  return undefined
}

export const getMembers = (accessPolicies: IAM_APIAccessPolicy[] = [], serviceAccounts: IAM_APIServiceAccount[] = [], tsgMap: { [id: string]: Tsg }) => {
  const policyMap = ARRAY.clean(accessPolicies).reduce((map: any, policy: any) => {
    const { id, principal, resource, principal_type, principal_display_name, source } = policy
    if (!map.hasOwnProperty(principal)) {
      map[principal] = {
        ids: [],
        identity_email: principal,
        display_name: principal_display_name,
        policies: [],
        roles: {},
        inherited_from: [],
        apps: [],
        identity_type: principal_type,
        sources: new Set()
      }
    }
    map[principal].ids.push(id)
    map[principal].sources.add(source || 'IAM')
    let resourceData
    if (typeof resource === 'string') {
      resourceData = prn.parse(resource)
    }
    else if (OBJECT.isObject(resource)) {
      resourceData = resource
    }

    const { app_id } = resourceData
    if (app_id) {
      map[principal].apps.push(app_id)
    }
    if (!map[principal].roles.hasOwnProperty(app_id)) {
      map[principal].roles[app_id] = []
    }

    policy.resource = resourceData
    const existingPolicy = map[principal].policies.find((item: any) => item.resource.app_id === policy.resource.app_id && item.resource.resource_scope === policy.resource.resource_scope && item.role === policy.role)
    if (existingPolicy) {
      const bottomTSG = selectLowerTSG(tsgMap[existingPolicy.inherited_from], tsgMap[policy.inherited_from])
      if (bottomTSG && bottomTSG?.id !== existingPolicy.inherited_from) {
        map[principal].policies = map[principal].policies.filter(({ id }: any) => id !== existingPolicy.id)
        map[principal].roles[app_id] = map[principal].roles[app_id].filter(({ id }: any) => id !== existingPolicy.id)
        map[principal].policies.push(policy)
        map[principal].roles[app_id].push(policy)
      }
    }
    else {
      map[principal].policies.push(policy)
      map[principal].roles[app_id].push(policy)
    }
    map[principal].policies = map[principal].policies.sort((a: any, b: any) => {
      if (a.resource.app_id === b.resource.app_id) {
        return a.resource.resource_scope > b.resource.resource_scope ? 1 : -1
      }
      return a.resource.app_id > b.resource.app_id ? 1 : -1
    })
    map[principal].apps = map[principal].apps.sort()
    map[principal].inherited_from = map[principal].policies.map((policy: any) => policy.inherited_from)
    return map
  }, {})
  ARRAY.clean(serviceAccounts).forEach(({ client_id, id, inherited_from, name, tsg_id, contact_email, description, source }: any) => {
    if (policyMap[client_id]) {
      policyMap[client_id] = {
        ...policyMap[client_id],
        name,
        tsg_id,
        client_id,
        id,
        contact_email,
        description,
        inherited_from: [...new Set([...policyMap[client_id].inherited_from, inherited_from])],
        sources: new Set()
      }
    }
    else {
      policyMap[client_id] = {
        ids: [],
        identity_email: client_id,
        display_name: name,
        policies: [],
        roles: {},
        inherited_from: [inherited_from],
        apps: [],
        identity_type: IDENTITY_TYPES.SERVICE_ACCOUNT,
        name,
        tsg_id,
        client_id,
        id,
        contact_email,
        description,
        sources: new Set()
      }
    }
    policyMap[client_id].sources.add(source || 'IAM')
  })
  return Object.values(policyMap)
}

export const getApps = (accessPolicies = [], getAppName: Function) => {
  return Object.values(ARRAY.clean(accessPolicies).reduce((map: any, policy: any) => {

    const { resource } = policy

    let resourceData
    if (typeof resource === 'string') {
      resourceData = prn.parse(resource)
    }
    else if (OBJECT.isObject(resource)) {
      resourceData = resource
    }

    const { app_id } = resourceData
    if (app_id && !map.hasOwnProperty(app_id)) {
      map[app_id] = { id: app_id, value: getAppName(app_id) }
    }

    return map
  }, {}))
}

export const getRoles = (accessPolicies = [], roleMap: { [key: string]: IAM_Role } = {}, customRoleMap: { [key: string]: IAM_Role } = {}) => {
  return Object.values(ARRAY.clean(accessPolicies).reduce((map: any, policy: any) => {
    const { role } = policy
    if (role && !map.hasOwnProperty(role)) {
      map[role] = { id: role, value: role }
    }
    if (roleMap[role]) {
      map[role].value = roleMap[role].label
    }
    if (customRoleMap[role]) {
      map[role].value = customRoleMap[role].name
    }
    return map
  }, {}))
}

export const getInherited = (accessPolicies: any[] = [], tsgMap: any = {}, selectedTreeNode: any = {}) => {
  return Object.values(ARRAY.clean(accessPolicies).reduce((map: any, policy: any) => {
    const { inherited_from } = policy
    if (inherited_from === selectedTreeNode?.id) {
      return map
    }
    if (inherited_from && !map.hasOwnProperty(inherited_from)) {
      map[inherited_from] = { id: inherited_from, value: tsgMap[inherited_from]?.display_name }
    }
    return map
  }, {}))
}

export const sortApps = (itemA: any, itemB: any, sortBy: any) => {
  const apps_a = itemA.apps.sort().join('')
  const apps_b = itemB.apps.sort().join('')
  return sortBy.desc ? apps_b.localeCompare(apps_a) : apps_a.localeCompare(apps_b)
}

export const sortRoles = (itemA: any, itemB: any, sortBy: any, roleMap: { [key: string]: IAM_Role }, customRoleMap: { [key: string]: IAM_Role }) => {
  const roles_a = [...new Set(_.flatten(Object.values(itemA?.roles || {})).map(({ role }: any) => (roleMap[role]?.label || customRoleMap[role]?.name || '')))].sort().join('')
  const roles_b = [...new Set(_.flatten(Object.values(itemB?.roles || {})).map(({ role }: any) => (roleMap[role]?.label || customRoleMap[role]?.name || '')))].sort().join('')
  return sortBy.desc ? roles_b.localeCompare(roles_a) : roles_a.localeCompare(roles_b)
}

export const sortInherited = (itemA: any, itemB: any, sortBy: any, tsgMap: { [key: string]: Tsg }) => {
  const tsgs_a = [...new Set(itemA.inherited_from)].map(tsg_id => tsgMap[String(tsg_id)]?.display_name || '').sort().join('')
  const tsgs_b = [...new Set(itemB.inherited_from)].map(tsg_id => tsgMap[String(tsg_id)]?.display_name || '').sort().join('')
  return sortBy.desc ? tsgs_b.localeCompare(tsgs_a) : tsgs_a.localeCompare(tsgs_b)
}

export const defaultSort = (itemA: any, itemB: any, sortBy: any) => {
  const stringA = itemA[sortBy?.id] || itemA?.identity_email
  const stringB = itemB[sortBy?.id] || itemB?.identity_email
  return sortBy.desc ? stringB.localeCompare(stringA) : stringA.localeCompare(stringB)
}
